'use client'

import { useRef, useState } from 'react'
import { Turnstile } from '@marsidev/react-turnstile'
import type { TurnstileInstance } from '@marsidev/react-turnstile'

type FormState = 'idle' | 'submitting' | 'success' | 'error'

type Fields = {
  name: string
  email: string
  company: string
  service: string
  message: string
}

type FieldErrors = Partial<Record<keyof Fields, string>>

const VALID_SERVICES = ['qa-strategy', 'tooling-automation', 'embedded-qa', 'ongoing-service', 'other']
const MAX_SHORT = 100
const MAX_MESSAGE = 2500

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validate(fields: Fields): FieldErrors {
  const errors: FieldErrors = {}

  if (!fields.name.trim()) {
    errors.name = 'Name is required.'
  } else if (fields.name.length > MAX_SHORT) {
    errors.name = `Name must be ${MAX_SHORT} characters or fewer.`
  }

  if (!fields.email.trim()) {
    errors.email = 'Email address is required.'
  } else if (!EMAIL_RE.test(fields.email)) {
    errors.email = 'Enter a valid email address.'
  } else if (fields.email.length > MAX_SHORT) {
    errors.email = `Email must be ${MAX_SHORT} characters or fewer.`
  }

  if (!fields.company.trim()) {
    errors.company = 'Company name is required.'
  } else if (fields.company.length > MAX_SHORT) {
    errors.company = `Company must be ${MAX_SHORT} characters or fewer.`
  }

  if (!fields.service || !VALID_SERVICES.includes(fields.service)) {
    errors.service = 'Please select a service you\'re interested in.'
  }

  if (!fields.message.trim()) {
    errors.message = 'Message is required.'
  } else if (fields.message.length > MAX_MESSAGE) {
    errors.message = `Message must be ${MAX_MESSAGE} characters or fewer.`
  }

  return errors
}

const FIELD_ORDER: (keyof Fields)[] = ['name', 'email', 'company', 'service', 'message']

const labelStyle = {
  display: 'block',
  fontSize: '10px',
  letterSpacing: '0.16em',
  color: '#52525B',
  fontFamily: 'var(--font-jetbrains)',
  marginBottom: '8px',
}

function fieldInputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%',
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: `1px solid ${hasError ? '#F87171' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '10px',
    padding: '13px 16px',
    color: '#F4F4F5',
    fontSize: '14px',
    fontFamily: 'var(--font-jakarta)',
    outline: 'none',
    transition: 'border-color 0.2s, background 0.2s',
  }
}

function FieldError({ message }: { message: string }) {
  return (
    <p className="mt-2 text-xs" style={{ color: '#F87171', fontFamily: 'var(--font-jakarta)' }}>
      {message}
    </p>
  )
}

export default function ContactForm() {
  const [state, setState] = useState<FormState>('idle')
  const [submitError, setSubmitError] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({})
  const [values, setValues] = useState<Fields>({ name: '', email: '', company: '', service: '', message: '' })
  const [honeypot, setHoneypot] = useState('')
  const turnstileRef = useRef<TurnstileInstance>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const fieldRefs = useRef<Partial<Record<keyof Fields, HTMLElement | null>>>({})

  function handleChange(field: keyof Fields, value: string) {
    const next = { ...values, [field]: value }
    setValues(next)
    if (touched[field]) {
      const errs = validate(next)
      setErrors((prev) => ({ ...prev, [field]: errs[field] }))
    }
  }

  function handleBlur(field: keyof Fields) {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const errs = validate(values)
    setErrors((prev) => ({ ...prev, [field]: errs[field] }))
  }

  function focusStyle(field: keyof Fields): React.CSSProperties {
    return {
      borderColor: errors[field] && touched[field] ? '#F87171' : 'rgba(129, 140, 248, 0.6)',
      background: 'rgba(255,255,255,0.09)',
    }
  }

  function blurStyle(field: keyof Fields): React.CSSProperties {
    return {
      borderColor: errors[field] && touched[field] ? '#F87171' : 'rgba(255,255,255,0.1)',
      background: 'rgba(255,255,255,0.06)',
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    // Mark all touched and validate
    const allTouched = Object.fromEntries(FIELD_ORDER.map((f) => [f, true]))
    setTouched(allTouched)
    const errs = validate(values)
    setErrors(errs)

    const firstErrorField = FIELD_ORDER.find((f) => errs[f])
    if (firstErrorField) {
      fieldRefs.current[firstErrorField]?.focus()
      return
    }

    if (!turnstileToken) {
      setSubmitError('Please complete the security check.')
      return
    }

    setState('submitting')
    setSubmitError('')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, turnstileToken, website: honeypot }),
      })

      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? 'Something went wrong')
      }

      setState('success')
    } catch (err) {
      setState('error')
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again or email us directly.')
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    }
  }

  if (state === 'success') {
    return (
      <div
        className="flex flex-col gap-4 py-16 text-center items-center justify-center"
        style={{ minHeight: '400px' }}
      >
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-2"
          style={{ background: 'rgba(129,140,248,0.12)', border: '1px solid rgba(129,140,248,0.3)' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#818CF8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 12l5 5L20 7"/>
          </svg>
        </div>
        <h3 className="text-2xl" style={{ fontFamily: 'var(--font-syne)', fontWeight: 700, color: '#F4F4F5' }}>
          Message sent
        </h3>
        <p className="text-sm" style={{ color: '#71717A', maxWidth: '320px', fontFamily: 'var(--font-jakarta)' }}>
          Thanks for getting in touch. We&rsquo;ll get back to you within one business day.
        </p>
      </div>
    )
  }

  const showError = (field: keyof Fields): boolean => !!(touched[field] && errors[field])
  const msgLen = values.message.length
  const msgNearLimit = msgLen >= MAX_MESSAGE - 200

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>

      {/* Name + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="name" style={labelStyle}>NAME *</label>
          <input
            ref={(el) => { fieldRefs.current.name = el }}
            id="name"
            name="name"
            type="text"
            maxLength={MAX_SHORT}
            value={values.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle('name'))}
            style={fieldInputStyle(showError('name'))}
            aria-describedby={showError('name') ? 'name-error' : undefined}
            aria-invalid={showError('name')}
          />
          {showError('name') && <FieldError message={errors.name!} />}
        </div>
        <div>
          <label htmlFor="email" style={labelStyle}>EMAIL *</label>
          <input
            ref={(el) => { fieldRefs.current.email = el }}
            id="email"
            name="email"
            type="email"
            maxLength={MAX_SHORT}
            value={values.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle('email'))}
            style={fieldInputStyle(showError('email'))}
            aria-describedby={showError('email') ? 'email-error' : undefined}
            aria-invalid={showError('email')}
          />
          {showError('email') && <FieldError message={errors.email!} />}
        </div>
      </div>

      {/* Company + Service */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="company" style={labelStyle}>COMPANY *</label>
          <input
            ref={(el) => { fieldRefs.current.company = el }}
            id="company"
            name="company"
            type="text"
            maxLength={MAX_SHORT}
            value={values.company}
            onChange={(e) => handleChange('company', e.target.value)}
            onBlur={() => handleBlur('company')}
            onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle('company'))}
            style={fieldInputStyle(showError('company'))}
            aria-describedby={showError('company') ? 'company-error' : undefined}
            aria-invalid={showError('company')}
          />
          {showError('company') && <FieldError message={errors.company!} />}
        </div>
        <div>
          <label htmlFor="service" style={labelStyle}>INTERESTED IN *</label>
          <select
            ref={(el) => { fieldRefs.current.service = el }}
            id="service"
            name="service"
            value={values.service}
            onChange={(e) => handleChange('service', e.target.value)}
            onBlur={() => handleBlur('service')}
            onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle('service'))}
            style={{ ...fieldInputStyle(showError('service')), appearance: 'none', cursor: 'pointer' }}
            aria-describedby={showError('service') ? 'service-error' : undefined}
            aria-invalid={showError('service')}
          >
            <option value="" disabled>Select a service…</option>
            <option value="qa-strategy">QA Strategy</option>
            <option value="tooling-automation">Tooling &amp; Automation</option>
            <option value="embedded-qa">Embedded QA</option>
            <option value="ongoing-service">Ongoing QA Service</option>
            <option value="other">Something else</option>
          </select>
          {showError('service') && <FieldError message={errors.service!} />}
        </div>
      </div>

      {/* Message */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <label htmlFor="message" style={{ ...labelStyle, marginBottom: 0 }}>YOUR MESSAGE *</label>
          <span
            className="text-xs tabular-nums"
            style={{
              fontFamily: 'var(--font-jetbrains)',
              color: msgLen > MAX_MESSAGE ? '#F87171' : msgNearLimit ? '#F59E0B' : '#A1A1AA',
            }}
          >
            {msgLen} / {MAX_MESSAGE}
          </span>
        </div>
        <textarea
          ref={(el) => { fieldRefs.current.message = el }}
          id="message"
          name="message"
          rows={6}
          maxLength={MAX_MESSAGE}
          value={values.message}
          onChange={(e) => handleChange('message', e.target.value)}
          onBlur={() => handleBlur('message')}
          onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle('message'))}
          style={{ ...fieldInputStyle(showError('message')), resize: 'vertical', lineHeight: '1.6' }}
          aria-describedby={showError('message') ? 'message-error' : undefined}
          aria-invalid={showError('message')}
        />
        {showError('message') && <FieldError message={errors.message!} />}
      </div>

      {/* Honeypot — hidden from real users, bots will fill it */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 0, height: 0, overflow: 'hidden' }}>
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      {/* Turnstile */}
      <Turnstile
        ref={turnstileRef}
        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ''}
        onSuccess={setTurnstileToken}
        onExpire={() => setTurnstileToken(null)}
        onError={() => setTurnstileToken(null)}
        options={{ theme: 'dark', size: 'normal' }}
      />

      {/* Submit-level error */}
      {(state === 'error' || submitError) && (
        <p
          className="text-sm px-4 py-3 rounded"
          style={{ color: '#F87171', backgroundColor: 'rgba(248, 113, 113, 0.08)', border: '1px solid rgba(248, 113, 113, 0.2)' }}
        >
          {submitError || 'Something went wrong. Please try again or email us directly.'}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={state === 'submitting'}
        className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 text-sm tracking-wide rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed bg-[#818CF8] hover:bg-[#6366F1] text-[#09090B]"
        style={{ fontFamily: 'var(--font-jakarta)', fontWeight: 600 }}
      >
        {state === 'submitting' ? 'Sending…' : 'Get In Touch'}
      </button>
    </form>
  )
}
